import { useEffect, useState, useCallback } from "react";
import { Icon, Avatar, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { fmtDate, fmtRelative } from "../../lib/dates.js";
import { formatMoney } from "../../lib/money.js";
import TableSkeleton from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";
import SubscriptionDetailDrawer from "../../components/admin/SubscriptionDetailDrawer.jsx";
import {
  listSubscriptions,
  listPlans,
  cancelSubscription,
  reactivateSub,
  runSubscriptionSweep,
  itemsOf,
} from "../../lib/adminApi";

// A paid subscription whose renewal date has already passed. The stored
// `status` may still read "active" until the daily sweep flips it, so we
// flag it visually right away.
const isLapsed = (s) =>
  s &&
  s.status !== "canceled" &&
  s.currentPeriodEnd &&
  new Date(s.currentPeriodEnd) < new Date();

const PAGE_SIZE = 30;

const FILTERS = [
  ["", "All"],
  ["active", "Active"],
  ["trialing", "Trialing"],
  ["past_due", "Past Due"],
  ["canceled", "Canceled"],
];

const STATUS_LABEL = {
  active: "Active", trialing: "Trialing", past_due: "Past Due",
  canceled: "Canceled", pending: "Pending",
};

const statusBadge = (s) =>
  s === "active"   ? "badge-success" :
  s === "trialing" ? "badge-warning" :
  s === "past_due" ? "badge-error"   :
  s === "canceled" ? "badge-muted"   : "badge-muted";

function SubscriptionsInner() {
  const { toast, openModal, closeModal, goPage } = useApp();
  const [resp, setResp] = useState({ items: [], total: 0, hasMore: false, page: 1 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [page, setPage] = useState(1);
  const [plans, setPlans] = useState([]);
  const [sweepAsOf, setSweepAsOf] = useState(""); // optional simulated date for the expiry sweep

  useEffect(() => { listPlans().then(setPlans).catch(() => setPlans([])); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (planFilter) params.set("plan", planFilter);
    params.set("page", String(page));
    params.set("limit", String(PAGE_SIZE));
    try {
      setResp(itemsOf(await listSubscriptions(`?${params}`)));
    } catch (e) {
      toast(e.message || "Failed to load subscriptions", "error");
    } finally {
      setLoading(false);
    }
  }, [status, planFilter, page, toast]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { setPage(1); }, [status, planFilter]);

  const subs = resp.items.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (s.company?.name || "").toLowerCase().includes(q) ||
      (s.company?.slug || "").toLowerCase().includes(q) ||
      (s.plan || "").toLowerCase().includes(q)
    );
  });

  const planAmount = (sub) => {
    if (sub.planAmount > 0) return sub.planAmount;
    const plan = plans.find((p) => p.key === sub.plan);
    return plan?.amount || 0;
  };

  const totalPages = Math.max(1, Math.ceil(resp.total / PAGE_SIZE));

  const onView = (s) => {
    openModal(
      <SubscriptionDetailDrawer subscriptionId={s.id} onClose={closeModal} />,
      "lg",
    );
  };

  const onCancel = (s) => {
    openModal(
      <>
        <div className="modal-head">
          <h3>Cancel subscription</h3>
          <button className="icon-btn" onClick={closeModal}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Cancel <strong>{s.company?.name}</strong>'s <strong>{s.plan}</strong> plan.
          </p>
        </div>
        <div className="modal-foot" style={{ gap: 8, flexWrap: "wrap" }}>
          <button className="btn btn-ghost" onClick={closeModal}>Keep</button>
          <button
            className="btn"
            onClick={async () => {
              try { await cancelSubscription(s.id, "period_end"); toast("Will cancel at period end", "success"); closeModal(); load(); }
              catch (e) { toast(e.message, "error"); }
            }}
          >
            <Icon name="calendar-x" size={14} /> At Period End
          </button>
          <button
            className="btn"
            style={{ background: "var(--error)", color: "#fff" }}
            onClick={async () => {
              try { await cancelSubscription(s.id, "now"); toast("Canceled immediately", "success"); closeModal(); load(); }
              catch (e) { toast(e.message, "error"); }
            }}
          >
            <Icon name="x-circle" size={14} /> Cancel Now
          </button>
        </div>
      </>,
    );
  };

  const onReactivate = (s) => {
    openModal(
      <>
        <div className="modal-head">
          <h3>Reactivate subscription?</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            <strong>{s.company?.name}</strong> will regain access immediately. Razorpay collection will resume.
          </p>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={async () => {
            try {
              await reactivateSub(s.id);
              toast("Reactivated", "success");
              closeModal();
              load();
            } catch (e) { toast(e.message, "error"); }
          }}>
            <Icon name="play" size={14} /> Reactivate
          </button>
        </div>
      </>,
    );
  };

  return (
    <section>
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 className="page-title">Subscriptions</h1>
          <p className="page-sub">{resp.total} subscriptions</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <input
            type="date"
            className="input"
            value={sweepAsOf}
            onChange={(e) => setSweepAsOf(e.target.value)}
            title="Optional: run the sweep as if it were this date (for testing). Leave empty to use today."
            style={{ height: 34, width: 150, fontSize: 13, padding: "0 10px" }}
          />
          <button
            className="btn btn-ghost btn-sm"
            title="Run the expiry sweep (lapse overdue plans → past due → block past the 3-day grace). Same logic as the daily job."
            onClick={async () => {
              try {
                const r = await runSubscriptionSweep(sweepAsOf || undefined);
                toast(`Sweep done — ${r.lapsed} lapsed, ${r.deactivated} blocked`, "success");
                load();
              } catch (e) {
                toast(e.message || "Sweep failed", "error");
              }
            }}
          >
            <Icon name="refresh-cw" size={14} /> Run expiry check
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
            <Icon
              name="search" size={14}
              style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }}
            />
            <input
              className="input"
              placeholder="Search company or plan…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 30 }}
            />
          </div>
          {FILTERS.map(([k, l]) => (
            <button
              key={k}
              className={`filter-chip ${status === k ? "active" : ""}`}
              onClick={() => setStatus(k)}
            >
              {l}
            </button>
          ))}
          <select
            className="input"
            style={{ height: 34, width: 160, fontSize: 13, padding: "0 10px" }}
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="">All plans</option>
            {plans.filter((p) => p.key !== "free_trial").map((p) => (
              <option key={p.key} value={p.key}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <TableSkeleton columns={7} rows={6} />
      ) : subs.length === 0 ? (
        <EmptyState title="No subscriptions" desc="No subscriptions match the filter." icon="repeat" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Company</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Period End</th>
                <th>Created</th>
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id}>
                  <td data-label="Company">
                    <div className="cell-emp">
                      <Avatar name={s.company?.name || "?"} size="sm" seed={(s.company?.name || "").length} />
                      <div>
                        <div className="name">{s.company?.name || "—"}</div>
                        <div className="sub">{s.company?.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td data-label="Plan"><span className="badge badge-primary">{s.plan}</span></td>
                  <td data-label="Status">
                    <span className={`badge ${statusBadge(s.status)}`}>
                      <span className="badge-dot" /> {STATUS_LABEL[s.status] || s.status}
                    </span>
                    {isLapsed(s) && s.status === "active" && (
                      <span className="badge badge-error" style={{ marginLeft: 6 }} title="Renewal date passed — not yet swept">
                        <span className="badge-dot" /> Expired
                      </span>
                    )}
                  </td>
                  <td data-label="Amount"><strong>{formatMoney(planAmount(s))}</strong></td>
                  <td
                    data-label="Period End"
                    style={{ fontSize: 12, color: isLapsed(s) ? "var(--error)" : undefined, fontWeight: isLapsed(s) ? 600 : undefined }}
                  >
                    {fmtDate(s.currentPeriodEnd)}{isLapsed(s) ? " · expired" : ""}
                  </td>
                  <td data-label="Created" style={{ fontSize: 12, color: "var(--text-muted)" }}>{fmtRelative(s.createdAt)}</td>
                  <td data-label="">
                    <div className="row-actions">
                      <button className="icon-btn" title="View" onClick={() => onView(s)}>
                        <Icon name="eye" size={14} />
                      </button>
                      {s.company?.id && (
                        <button
                          className="icon-btn"
                          title="Open company"
                          onClick={() => goPage("adminCompanyDetail", { companyId: s.company.id })}
                        >
                          <Icon name="external-link" size={14} />
                        </button>
                      )}
                      {s.status === "canceled" ? (
                        <button className="icon-btn" title="Reactivate" onClick={() => onReactivate(s)}>
                          <Icon name="play" size={14} />
                        </button>
                      ) : (
                        <button className="icon-btn" title="Cancel" onClick={() => onCancel(s)}>
                          <Icon name="x-circle" size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && resp.total > 0 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 16, alignItems: "center" }}>
          <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <Icon name="chevron-left" size={14} /> Prev
          </button>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Page {page} of {totalPages}
          </span>
          <button className="btn btn-ghost btn-sm" disabled={!resp.hasMore} onClick={() => setPage((p) => p + 1)}>
            Next <Icon name="chevron-right" size={14} />
          </button>
        </div>
      )}
    </section>
  );
}

export default function AdminSubscriptions() {
  return (
    <AdminPageBoundary>
      <SubscriptionsInner />
    </AdminPageBoundary>
  );
}
